import { Check, Copy } from "@phosphor-icons/react";
import { useState } from "react";
import { agentInstructions, clients, type Locale } from "../data";

type SetupPanelProps = {
  locale: Locale;
  copyText: (text: string, key: string) => Promise<void>;
  copiedKey: string | null;
};

export function SetupPanel({ locale, copyText, copiedKey }: SetupPanelProps) {
  const [selected, setSelected] = useState<(typeof clients)[number]["id"]>("codex");
  const client = clients.find((item) => item.id === selected) ?? clients[0];
  const copyLabel = (key: string) => copiedKey === key
    ? (locale === "en" ? "Copied" : "已复制")
    : (locale === "en" ? "Copy" : "复制");

  return (
    <div className="setup-stack">
      <div className="setup-panel">
        <div className="client-tabs" role="tablist" aria-label={locale === "en" ? "MCP client setup" : "MCP 客户端设置"}>
          {clients.map((item) => (
            <button
              className={selected === item.id ? "client-tab active" : "client-tab"}
              key={item.id}
              onClick={() => setSelected(item.id)}
              role="tab"
              aria-selected={selected === item.id}
              type="button"
            >
              {item.name}
            </button>
          ))}
        </div>

        <div className="setup-steps" role="tabpanel">
          <article className="setup-step">
            <span className="step-number">1</span>
            <div className="step-content">
              <h3>{client.setupTitle[locale]}</h3>
              <p>{client.note[locale]}</p>
              <div className="inline-code-block">
                <pre><code>{client.code}</code></pre>
                <button className="copy-control" onClick={() => copyText(client.code, `client-${client.id}`)} type="button">
                  {copiedKey === `client-${client.id}` ? <Check size={16} /> : <Copy size={16} />}
                  {copyLabel(`client-${client.id}`)}
                </button>
              </div>
            </div>
          </article>

          <article className="setup-step">
            <span className="step-number">2</span>
            <div className="step-content">
              <h3>{client.verifyTitle[locale]}</h3>
              <p>{client.verifyNote[locale]}</p>
              {client.verifyCode ? (
                <div className="inline-code-block compact">
                  <pre><code>{client.verifyCode}</code></pre>
                  <button className="copy-control" onClick={() => copyText(client.verifyCode, `verify-${client.id}`)} type="button">
                    {copiedKey === `verify-${client.id}` ? <Check size={16} /> : <Copy size={16} />}
                    {copyLabel(`verify-${client.id}`)}
                  </button>
                </div>
              ) : null}
            </div>
          </article>

          {client.showProjectInstructions ? (
            <article className="setup-step instruction-step">
              <span className="step-number">3</span>
              <div className="step-content">
                <h3>{locale === "en" ? "Add project instructions" : "添加项目指令"}</h3>
                <p>
                  {locale === "en"
                    ? <>Put the agent instructions below in <code>{client.instructionFile}</code> when you want Codex to route BlockRazor work through this MCP server consistently.</>
                    : <>如需 Codex 持续通过此 MCP 服务处理 BlockRazor 工作，请将下方 Agent Instructions 放入 <code>{client.instructionFile}</code>。</>}
                </p>
              </div>
            </article>
          ) : null}
        </div>
      </div>

      <section className="instructions-panel" aria-labelledby="agent-instructions-title">
        <div className="instructions-heading">
          <div>
            <span className="eyebrow">{locale === "en" ? "Project memory" : "项目记忆"}</span>
            <h3 id="agent-instructions-title">Agent Instructions</h3>
            <p>
              {locale === "en"
                ? <>Add this to <code>{client.instructionFile}</code>.</>
                : <>将以下内容添加到 <code>{client.instructionFile}</code>。</>}
            </p>
          </div>
          <button className="copy-control" onClick={() => copyText(agentInstructions[locale], "agent-instructions")} type="button">
            {copiedKey === "agent-instructions" ? <Check size={16} /> : <Copy size={16} />}
            {copyLabel("agent-instructions")}
          </button>
        </div>
        <pre className="instructions-code"><code>{agentInstructions[locale]}</code></pre>
      </section>
    </div>
  );
}
